import { useState, useEffect, useCallback, useRef } from 'react'
import {
  MapPin,
  Users,
  Truck,
  RefreshCw,
  Loader2,
  Search,
  Filter,
  Clock,
  Phone,
  Navigation,
  Map as MapIcon,
  List,
} from 'lucide-react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import toast from 'react-hot-toast'
import { fetchMapData } from '../services/adminService'
import { useAuthStore } from '../store/authStore'
import { connectTracker, subscribeToCollector, onCollectorLocation, disconnectTracker } from '../services/socketService'

type ItemType = 'collector' | 'resident' | 'pickup'
type FilterTab = 'all' | 'collectors' | 'residents' | 'pickups'
type ViewMode = 'map' | 'table'

interface MapItem {
  id: string
  type: ItemType
  name: string
  phone?: string
  address?: string
  zone?: string
  status?: string
  lat?: number
  lng?: number
}

const TYPE_STYLES: Record<ItemType, { bg: string; text: string; label: string }> = {
  collector: { bg: 'bg-green-100', text: 'text-green-700', label: 'Collector' },
  resident: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Resident' },
  pickup: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Pickup' },
}

const FILTER_TABS: { value: FilterTab; label: string; icon: React.ElementType }[] = [
  { value: 'all', label: 'All', icon: MapPin },
  { value: 'collectors', label: 'Collectors', icon: Truck },
  { value: 'residents', label: 'Residents', icon: Users },
  { value: 'pickups', label: 'Active Pickups', icon: Clock },
]

// Custom Leaflet marker icons
function createIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="
      width: 28px; height: 28px; border-radius: 50%; border: 3px solid white;
      background: ${color}; box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      display: flex; align-items: center; justify-content: center;
    "></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16],
  })
}

const MARKER_ICONS: Record<ItemType, L.DivIcon> = {
  collector: createIcon('#16a34a'),
  resident: createIcon('#2563eb'),
  pickup: createIcon('#ea580c'),
}

// Accra, Ghana default center
const DEFAULT_CENTER: [number, number] = [5.6037, -0.1870]
const DEFAULT_ZOOM = 12

// Component to auto-fit map bounds to markers
function FitBounds({ items }: { items: MapItem[] }) {
  const map = useMap()
  useEffect(() => {
    const coords = items.filter((i) => i.lat && i.lng).map((i) => [i.lat!, i.lng!] as [number, number])
    if (coords.length > 0) {
      const bounds = L.latLngBounds(coords)
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 })
    }
  }, [items, map])
  return null
}

export default function MapView() {
  const user = useAuthStore((s) => s.user)
  const isSuperAdmin = user?.isSuperAdmin ?? false
  const assignedZones = user?.assignedZones ?? []

  const [items, setItems] = useState<MapItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filterTab, setFilterTab] = useState<FilterTab>('all')
  const [zoneFilter, setZoneFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('map')
  const [liveCollectors, setLiveCollectors] = useState<Set<string>>(new Set())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const token = useAuthStore((s) => s.token)

  const loadData = useCallback(async () => {
    try {
      const typeParam = filterTab === 'all' ? 'all' : filterTab
      const data = await fetchMapData(typeParam)

      const normalized: MapItem[] = []

      // Process collectors
      if (data?.collectors) {
        for (const c of data.collectors) {
          normalized.push({
            id: c.id ?? c.userId,
            type: 'collector',
            name: c.name ?? c.user?.name ?? 'Unknown',
            phone: c.phone ?? c.user?.phone ?? '',
            address: c.address ?? c.user?.address ?? '',
            zone: c.zone ?? c.assignedZone ?? '',
            status: c.isActive !== undefined ? (c.isActive ? 'Active' : 'Inactive') : (c.status ?? 'Unknown'),
            lat: c.lat ?? c.latitude ?? c.location?.lat,
            lng: c.lng ?? c.longitude ?? c.location?.lng,
          })
        }
      }

      // Process residents
      if (data?.residents) {
        for (const r of data.residents) {
          normalized.push({
            id: r.id ?? r.userId,
            type: 'resident',
            name: r.name ?? r.user?.name ?? 'Unknown',
            phone: r.phone ?? r.user?.phone ?? '',
            address: r.address ?? r.user?.address ?? '',
            zone: r.zone ?? '',
            status: r.isActive !== undefined ? (r.isActive ? 'Active' : 'Inactive') : (r.status ?? 'Active'),
            lat: r.lat ?? r.latitude ?? r.location?.lat,
            lng: r.lng ?? r.longitude ?? r.location?.lng,
          })
        }
      }

      // Process pickups
      if (data?.pickups) {
        for (const p of data.pickups) {
          normalized.push({
            id: p.id,
            type: 'pickup',
            name: p.residentName ?? p.user?.name ?? `Pickup #${p.id.slice(0, 6)}`,
            phone: p.phone ?? p.user?.phone ?? '',
            address: p.address ?? p.pickupAddress ?? '',
            zone: p.zone ?? '',
            status: p.status ?? 'Pending',
            lat: p.lat ?? p.latitude ?? p.location?.lat,
            lng: p.lng ?? p.longitude ?? p.location?.lng,
          })
        }
      }

      // GET /admin/map-data answers { items, count }, each item typed
      // 'user' | 'collector' | 'pickup'. The page kept reading the older
      // { collectors, residents, pickups } shape and drew an empty map.
      const flat = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : null
      if (flat) {
        for (const item of flat) {
          normalized.push({
            id: item.id,
            type: (item.type === 'user' ? 'resident' : item.type ?? 'resident') as ItemType,
            name: item.name ?? 'Unknown',
            phone: item.phone ?? '',
            address: item.address ?? '',
            zone: item.zone ?? '',
            status: item.status ?? 'Unknown',
            lat: item.lat ?? item.latitude,
            lng: item.lng ?? item.longitude,
          })
        }
      }

      setItems(normalized)
    } catch {
      toast.error('Failed to load map data')
    } finally {
      setLoading(false)
    }
  }, [filterTab])

  useEffect(() => {
    setLoading(true)
    loadData()
  }, [loadData])

  // Auto-refresh logic
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        loadData()
      }, 30000)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [autoRefresh, loadData])

  // Socket.IO real-time collector tracking
  useEffect(() => {
    if (!token) return

    connectTracker(token)

    // Subscribe to all collectors currently loaded
    const collectorIds = items.filter((i) => i.type === 'collector').map((i) => i.id)
    const cleanups: Array<() => void> = []

    for (const id of collectorIds) {
      subscribeToCollector(id)
      const unsub = onCollectorLocation(id, (data) => {
        setItems((prev) =>
          prev.map((item) =>
            item.id === data.collectorId
              ? { ...item, lat: data.lat, lng: data.lng }
              : item
          )
        )
        setLiveCollectors((prev) => new Set(prev).add(data.collectorId))
      })
      cleanups.push(unsub)
    }

    return () => {
      cleanups.forEach((fn) => fn())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, items.length]) // Re-subscribe when collector count changes; items itself churns every poll

  // Disconnect tracker on full unmount
  useEffect(() => {
    return () => { disconnectTracker() }
  }, [])

  // Compute available zones from data
  const allZones = Array.from(new Set(items.map((i) => i.zone).filter(Boolean))) as string[]

  // Filter items
  const filtered = items.filter((item) => {
    if (zoneFilter !== 'all' && item.zone !== zoneFilter) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      const match =
        item.name.toLowerCase().includes(q) ||
        (item.phone ?? '').toLowerCase().includes(q) ||
        (item.address ?? '').toLowerCase().includes(q) ||
        (item.zone ?? '').toLowerCase().includes(q)
      if (!match) return false
    }
    return true
  })

  const geoItems = filtered.filter((i) => i.lat != null && i.lng != null)

  // Stats
  const collectorCount = filtered.filter((i) => i.type === 'collector').length
  const residentCount = filtered.filter((i) => i.type === 'resident').length
  const pickupCount = filtered.filter((i) => i.type === 'pickup').length

  const handleManualRefresh = () => {
    setLoading(true)
    loadData()
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A2E]">Operations Map</h2>
          <p className="text-sm text-[#6B7280] mt-0.5">
            {isSuperAdmin
              ? 'All Zones \u2014 Full operational overview'
              : `Your Zones: ${assignedZones.length > 0 ? assignedZones.join(', ') : 'None assigned'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex bg-[#F8FAF9] rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1 px-3 py-2 text-xs font-semibold transition-colors ${
                viewMode === 'map' ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-100'
              }`}
            >
              <MapIcon size={14} />
              Map
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1 px-3 py-2 text-xs font-semibold transition-colors ${
                viewMode === 'table' ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-100'
              }`}
            >
              <List size={14} />
              Table
            </button>
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#6B7280] bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border transition-colors ${
              autoRefresh
                ? 'bg-primary-600 text-white border-primary-600'
                : 'text-[#6B7280] bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Clock size={14} />
            Auto (30s)
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Collectors', value: collectorCount, icon: Truck, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Total Residents', value: residentCount, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Active Pickups', value: pickupCount, icon: MapPin, color: 'text-orange-600', bg: 'bg-orange-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <div>
              <p className="text-xs text-[#6B7280] font-medium">{label}</p>
              <p className="text-xl font-bold text-[#1A1A2E]">{value.toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 flex items-center gap-2 bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-[#6B7280]" />
            <input
              type="text"
              placeholder="Search by name, phone, address, zone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none"
            />
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#6B7280]" />
            <div className="flex bg-[#F8FAF9] rounded-xl border border-gray-200 overflow-hidden">
              {FILTER_TABS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setFilterTab(value)}
                  className={`px-3 py-2 text-xs font-semibold transition-colors whitespace-nowrap ${
                    filterTab === value
                      ? 'bg-primary-600 text-white'
                      : 'text-[#6B7280] hover:bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Zone filter (Super Admin only) */}
          {isSuperAdmin && allZones.length > 0 && (
            <select
              value={zoneFilter}
              onChange={(e) => setZoneFilter(e.target.value)}
              title="Filter by zone"
              aria-label="Filter by zone"
              className="border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-[#6B7280] bg-white outline-none focus:border-primary-500"
            >
              <option value="all">All Zones</option>
              {allZones.map((zone) => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-[#1A1A2E]">
              {geoItems.length} of {filtered.length} items with coordinates
            </p>
            {autoRefresh && (
              <span className="flex items-center gap-1 text-[10px] text-primary-600 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-600 animate-pulse" />
                Auto-refreshing
              </span>
            )}
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : (
            <div style={{ height: '520px' }}>
              <MapContainer
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds items={geoItems} />
                {geoItems.map((item) => (
                  <Marker
                    key={`${item.type}-${item.id}`}
                    position={[item.lat!, item.lng!]}
                    icon={MARKER_ICONS[item.type]}
                  >
                    <Popup>
                      <div className="text-xs min-w-[180px]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${TYPE_STYLES[item.type].bg} ${TYPE_STYLES[item.type].text}`}>
                            {TYPE_STYLES[item.type].label}
                          </span>
                          {item.status && (
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                              item.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {item.status}
                            </span>
                          )}
                          {item.type === 'collector' && liveCollectors.has(item.id) && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              Live
                            </span>
                          )}
                        </div>
                        <p className="font-bold text-[#1A1A2E] text-sm">{item.name}</p>
                        {item.phone && <p className="text-[#6B7280] mt-0.5">{item.phone}</p>}
                        {item.address && <p className="text-[#6B7280] mt-0.5 truncate max-w-[200px]">{item.address}</p>}
                        {item.zone && <p className="text-[#6B7280] mt-0.5">Zone: {item.zone}</p>}
                        <p className="text-[#6B7280] mt-0.5 font-mono">{item.lat!.toFixed(5)}, {item.lng!.toFixed(5)}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
          {/* Legend */}
          <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-4">
            {[
              { color: '#16a34a', label: 'Collector' },
              { color: '#2563eb', label: 'Resident' },
              { color: '#ea580c', label: 'Pickup' },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <span style={{ background: color }} className="w-3 h-3 rounded-full inline-block border border-white shadow-sm" />
                <span className="text-xs text-[#6B7280] font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-[#1A1A2E]">
              {filtered.length} item{filtered.length !== 1 ? 's' : ''}
            </p>
            {autoRefresh && (
              <span className="flex items-center gap-1 text-[10px] text-primary-600 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-600 animate-pulse" />
                Auto-refreshing
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F8FAF9]">
                    {['Type', 'Name', 'Phone', 'Address', 'Zone', 'Status', 'Coordinates'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((item) => {
                    const style = TYPE_STYLES[item.type]
                    return (
                      <tr key={`${item.type}-${item.id}`} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
                            {item.type === 'collector' && <Truck size={11} />}
                            {item.type === 'resident' && <Users size={11} />}
                            {item.type === 'pickup' && <MapPin size={11} />}
                            {style.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-[#1A1A2E] whitespace-nowrap">{item.name}</p>
                        </td>
                        <td className="px-4 py-3">
                          {item.phone ? (
                            <span className="flex items-center gap-1 text-xs text-[#6B7280] whitespace-nowrap">
                              <Phone size={11} />
                              {item.phone}
                            </span>
                          ) : (
                            <span className="text-xs text-[#6B7280]">--</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#6B7280] max-w-[200px] truncate">
                          {item.address || '--'}
                        </td>
                        <td className="px-4 py-3">
                          {item.zone ? (
                            <span className="text-xs font-medium text-[#6B7280] bg-gray-100 px-2 py-0.5 rounded-full">
                              {item.zone}
                            </span>
                          ) : (
                            <span className="text-xs text-[#6B7280]">--</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            item.status === 'Active' || item.status === 'COMPLETED'
                              ? 'bg-green-100 text-green-700'
                              : item.status === 'Pending' || item.status === 'PENDING'
                              ? 'bg-yellow-100 text-yellow-700'
                              : item.status === 'Inactive' || item.status === 'CANCELLED'
                              ? 'bg-red-100 text-red-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {item.lat && item.lng ? (
                            <span className="flex items-center gap-1 text-xs text-[#6B7280] font-mono whitespace-nowrap">
                              <Navigation size={11} className="text-primary-600" />
                              {item.lat.toFixed(4)}, {item.lng.toFixed(4)}
                            </span>
                          ) : (
                            <span className="text-xs text-[#6B7280] italic">No coordinates</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center">
                        <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-[#6B7280]">No items found</p>
                        <p className="text-xs text-[#6B7280] mt-0.5">Try adjusting your filters or search query</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
